import { SettingsSection } from "@/components/molecules/settings-section";
import { Button } from "@/components/ui/button";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import {
  useOnboarding,
  useRestartOnboarding,
} from "@/features/onboarding/hooks/use-onboarding";
import { useTranslation } from "@/features/utils/i18n";
import { SparkleIcon } from "@phosphor-icons/react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

/**
 * Run the guided setup again.
 *
 * The wizard is a one-shot by design, which leaves two people stuck: the client
 * who skipped it on day one and now wants it, and support looking at a tenant
 * that was set up wrong. Both need the same thing — the row back in `pending` —
 * so this is one button, gated on the permission that already decides who may
 * reconfigure the tenant.
 *
 * It navigates the person who pressed it straight into the wizard, and leaves
 * it armed for the next owner who signs in, which is what makes it usable as a
 * reset from support's side without them having to walk the client through it.
 */
export function RestartOnboardingSection() {
  const { t } = useTranslation("companySettings");
  const { company: clientId } = useParams<{ company: string }>();
  const navigate = useNavigate();
  const isOwner = useHasRole("OWNER");

  const { data: state } = useOnboarding();
  const restart = useRestartOnboarding();

  if (!isOwner) return null;

  const alreadyPending = state?.onboarding.status === "pending";

  const handleRestart = async () => {
    try {
      if (!alreadyPending) await restart.mutateAsync();
      navigate(`/${clientId}/onboarding`);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("companySettings.onboarding.failed"),
      );
    }
  };

  return (
    <SettingsSection
      title={t("companySettings.onboarding.title")}
      description={t("companySettings.onboarding.description")}
    >
      <div>
        <Button
          type="button"
          variant="outline"
          onClick={() => void handleRestart()}
          disabled={restart.isPending}
        >
          <SparkleIcon className="h-4 w-4" />
          {t("companySettings.onboarding.action")}
        </Button>
      </div>
    </SettingsSection>
  );
}
